import { useDispatch, useSelector } from "react-redux";
import { handleError } from "@common/components/helpers/error";
import LoadingAction from "@common/redux/actions/LoadingAction";
import CustomerReviewFiltersAction from "@common/redux/actions/CustomerReviewFiltersAction";
import CustomerInteractionClient from "@common/api/CustomerInteractionClient";
import CustomerClient from "@common/api/CustomerClient";
import useOrganisationTree from "./useOrganisationTree";
import CustomerReviewModalAction from "@common/redux/actions/CustomerReviewModalAction";
import ReviewClient from "@common/api/ReviewClient";
import useCustomers from "@common/components/hooks/useCustomers";
import { fromDate, toDate } from "@common/components/helpers/datetime";
import { fromDateTime, toDateTime } from "@common/components/helpers/datetime";
import usePhoto from "@common/components/hooks/usePhoto";
import {
  addBase64Prefix,
  getLatestImage,
} from "@common/components/helpers/image";
import ConfirmModalAction from "@common/redux/actions/ConfirmModalAction";
import MergeCustomersModalAction from "@common/redux/actions/MergeCustomersModalAction";
import useCustomerEdit from "@common/components/hooks/useCustomerEdit";
import { fetchCustomerRowVersion } from "@common/components/helpers/Customer/searchCustomer";
import { mapStatusCounts } from "@common/components/helpers/forms";
import { toggleStatusItem } from "@common/components/hooks/customerInteractionAdmin/useReviewsFilters";
import {
  mapCustomerStatusOption,
  mapStatusToCustomer,
  mapCustomerReviewStatus,
} from "@common/components/helpers/labels";

export default function useCustomerView() {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.customerTab);
  const {
    customerName,
    pageNumber,
    totalRecords,
    sortableColumns,
    updatedSortColumn,
    records,
    customerStatusTypes,
    selectedCustomer,
    selectedCustomerState,
    reviewStatusIds,
    lastInteractionDateFrom,
    lastInteractionDateTo,
  } = data;

  const modal = useSelector((state) =>
    CustomerReviewModalAction.getStatePath(state),
  );
  const { customerStatusOptions, selectedCustomerStatus } = modal;

  const setCustomerReviewModalState = CustomerReviewModalAction.dispatch;
  const setCustomerReviewFiltersState = CustomerReviewFiltersAction.dispatch;

  const { setPhotoState } = usePhoto();
  const { interactionDateRange } = useCustomers();

  const {
    id,
    triggeredByAlert,
    customerInteractionReasonId,
    dateRange,
    durationFrom,
    durationTo,
    netLossFrom,
    netLossTo,
    spendFrom,
    spendTo,
    reviewStatusTypeIds,
    isEscalated,
  } = useSelector((state) => CustomerReviewFiltersAction.getStatePath(state));

  const pageSize = 25;
  const pagination = {
    pageSize,
    totalPages: Math.ceil(totalRecords / pageSize),
    currentItem: (pageNumber - 1) * pageSize + 1,
    lastItem: Math.min(pageNumber * pageSize, totalRecords),
  };

  const { onOrgTreeSelectionChange } = useOrganisationTree();

  const {
    filterSelected: { operator, company, subcompany, site },
  } = useSelector((state) => state.customerFilters);

  const onOrgTreeChange = () => {
    onOrgTreeSelectionChange(operator, company);
  };

  const setState = (payload) => dispatch({ type: "CUSTOMER_TAB", payload });

  const getRecords = ({
    pageNumber = 1,
    byCustomer = true,
    customerId = null,
    reviewStatusIds = [1,2,3,4,5,6,-1],
    fetchCustomerDetails = true,
  }) => {
    LoadingAction.set(true).then(() => {
      CustomerInteractionClient.Search({
        byCustomer,
        customerId,
        siteId: site?.id,
        pageSize,
        pageNumber,
        reviewStatusIds,
        customerName,
        sortColumns: [updatedSortColumn] ?? sortableColumns,
        customerStateIds: selectedCustomerState ? [selectedCustomerState] : [],
        lastInteractionDateFrom: lastInteractionDateFrom ? `${lastInteractionDateFrom}T00:00:00` : null,
        lastInteractionDateTo: lastInteractionDateTo ? `${lastInteractionDateTo}T23:59:59` : null,
      })
        .then((response) => {
          if (!customerStatusTypes || !sortableColumns) getMetadata();
          if (selectedCustomer && fetchCustomerDetails)
            getCustomerDetails(selectedCustomer.customer);
          setState({
            reviewStatusCounts: mapStatusCounts(response?.reviewStatusCounts),
            totalRecords: response?.totalRecords,
            records: response?.data,
            selectedCustomer: null,
            activeViewTab: null,
            hideViewTab: true,
          });
          setCustomerReviewFiltersState({ showFilters: false });
        })
        .catch(handleError)
        .finally(() => LoadingAction.set(false));
    });
  };

  const getMetadata = () => {
    CustomerInteractionClient.ReadForSearch().then(
      ({ customerStatusTypes, sortableColumns }) => {
        setState({
          customerStatusTypes: customerStatusTypes.map((x) => {
            return { text: x.name, value: x.id };
          }),
          sortableColumns,
        });
      },
    );
  };

  const getCustomerDetails = (customer) => {
    CustomerClient.UpdateMetaData(customer?.id).then(({ record }) => {
      const customerStatus = mapCustomerStatusOption({
        customer,
        customerStatusOptions,
      });

      setState({ customerStatus });
      setCustomerReviewModalState({ customerStatus });

      setPhotoState({
        photo: addBase64Prefix(getLatestImage(record)?.image),
        updatePhoto: false,
      });
    });
  };

  const getCustomerInteractions = (customerId = null) => {
    LoadingAction.set(true).then(() => {
      CustomerInteractionClient.Search({
        id: id || null,
        byCustomer: false,
        customerId,
        siteId: site?.id,
        interactionDateFrom: fromDateTime(interactionDateRange),
        interactionDateTo: toDateTime(interactionDateRange),
        customerInteractionReasonId,
        triggeredByAlert,
      })
        .then((response) => {
          setState({
            customerEvents: response?.data,
            detailsTotalRecords: response?.totalRecords,
          });
        })
        .catch(handleError)
        .finally(() => LoadingAction.set(false));
    });
  };

  const getCustomerSessions = (customerId) => {
    const payload = Object.fromEntries(
      Object.entries({
        id: Number(id) || null,
        customerId,
        sessionStartFrom: fromDateTime(dateRange),
        sessionStartTo: toDateTime(dateRange),
        durationFrom: parseFloat(durationFrom),
        durationTo: parseFloat(durationTo),
        netLossFrom: parseFloat(netLossFrom),
        netLossTo: parseFloat(netLossTo),
        spendFrom: parseFloat(spendFrom),
        spendTo: parseFloat(spendTo),
      }).filter(([_, v]) => v !== undefined && v !== null && v !== ""),
    );

    LoadingAction.set(true).then(() => {
      CustomerClient.Sessions(payload)
        .then((response) => {
          setState({ customerSessions: response });
        })
        .catch(handleError)
        .finally(() => LoadingAction.set(false));
    });
  };

  const getCustomerReviews = (customerId) => {
    const payload = {
      id: Number(id) || null,
      customerId,
      reviewStatusTypeIds,
      reviewDateFrom: fromDate(dateRange),
      reviewDateTo: toDate(dateRange),
      isEscalated,
    };

    LoadingAction.set(true)
      .then(() => {
        ReviewClient.Search(payload).then((response) => {
          setState({ customerReviews: response });
        });
      })
      .catch(handleError)
      .finally(() => LoadingAction.set(false));
  };

  const fetchRecords = ({ selectedCustomer, activeViewTab }) => {
    switch (activeViewTab) {
      case 1:
        getCustomerInteractions(selectedCustomer?.customer?.id);
        break;
      case 2:
        getCustomerSessions(selectedCustomer?.customer?.id);
        break;
      case 3:
        getCustomerReviews(selectedCustomer?.customer?.id);
        break;
      default:
        return;
    }
  };

  const onInputChange = (_, { name, value }) => setState({ [name]: value });

  const onPageChange = (_e, { activePage }) => {
    setState({ pageNumber: activePage });
  };

  const onRecordClick = (selectedRecord) => {
    setState({ selectedRecord });
  };

  const onCustomerClick = ({ selectedCustomer, activeViewTab = null }) => {
    fetchCustomerRowVersion(selectedCustomer.customer?.id).then(
      (rowVersion) => {
        getCustomerDetails(selectedCustomer.customer);
        setState({ selectedCustomer, hideDetails: false });
        setCustomerReviewModalState({
          selectedCustomer: {
            ...selectedCustomer.customer,
            rowVersion,
          },
          returnDate: selectedCustomer.customer?.returnDate,
        });
        fetchRecords({ selectedCustomer, activeViewTab });
      },
    );
  };

  const onViewClick = ({ activeViewTab, selectedCustomer }) => {
    setState({ activeViewTab, hideViewTab: false });
    setCustomerReviewModalState({ currentTab: activeViewTab });
    fetchRecords({ activeViewTab, selectedCustomer });
  };

  const handleEditCustomer = async (customer) => {
    const rowVersion = await fetchCustomerRowVersion(customer?.id);
    await updateCustomer({ ...customer, rowVersion });

    const updatedRecords = records.map((record) =>
      record.customer?.id === customer.id ? { ...record, customer } : record,
    );

    setState({ records: updatedRecords });
  };

  const { updateCustomer, editCustomer } = useCustomerEdit({
    handleEditCustomer,
    customers: records?.map((x) => x.customer),
  });

  const mergeCustomer = () => {
    if (!selectedCustomer?.customer) return;
    MergeCustomersModalAction.open(selectedCustomer, {
      onMergeComplete: () => {
        setState({ selectedCustomer: null, hideDetails: true });
        getRecords({ pageNumber: 1, fetchCustomerDetails: false });
      },
    });
  };

  const onDeleteCustomer = (customerId) => {
    ConfirmModalAction.open(
      `Are you sure you wish to delete this customer #${customerId}?`,
      true,
      async () => {
        CustomerClient.Delete(customerId)
          .then(() => {
            setState({ selectedCustomer: null, hideDetails: true });
          })
          .catch(handleError)
          .finally(() => {
            getRecords({
              pageNumber: 1,
              fetchCustomerDetails: false,
            });
          });
      },
      async () => {},
      true,
    );
  };

  const onStatusChange = (id) => {
    const updatedStatuses = toggleStatusItem(id, reviewStatusIds);
    setState({ reviewStatusIds: updatedStatuses, pageNumber: 1 });
  };

  const onCustomerStatusChange = (_, { name, value }) => {
    const selectedCustomerStatus = customerStatusOptions.find(
      (x) => x.value === value,
    );

    setCustomerReviewModalState({
      selectedCustomerStatus,
      openStatusChangeModal: true,
    });

    // const updatedCustomer = mapStatusToCustomer({
    //   customerStatus: selectedCustomerStatus,
    //   customer: selectedCustomer?.customer,
    // });

    // const updatedRecords = records.map((record) =>
    //   record.id === selectedCustomer?.id
    //     ? { ...record, customer: updatedCustomer }
    //     : record,
    // );

    // setState({
    //   records: updatedRecords,
    //   selectedCustomer: {
    //     customer: updatedCustomer,
    //   },
    // });
  };

  const statusOptions = [
    { value: "due", text: "Due" },
    { value: "notDue", text: "Not Due" },
  ];

  const { complianceOnly } = useSelector((state) => state.logIn);

  const tabOptions = [
    { value: 1, text: "View Interactions" },
    ...(!complianceOnly ? [{ value: 2, text: "View Linked Sessions" }] : []),
    { value: 3, text: "View Reviews" },
    { value: 4, text: "View History" },
    { value: 5, text: "Comments" },
  ];

  const forename = selectedCustomer?.customer.forename;
  const surname = selectedCustomer?.customer.surname;
  const fullName = `${forename ?? ""} ${surname ?? ""}`;

  return {
    ...modal,
    ...data,
    operator,
    companyId: company.id,
    subcompanyId: subcompany.id,
    siteId: site.id,
    pagination,
    setState,
    getRecords,
    onOrgTreeChange,
    onInputChange,
    onPageChange,
    onCustomerClick,
    onRecordClick,
    onViewClick,
    editCustomer,
    mergeCustomer,
    onStatusChange,
    onDeleteCustomer,
    statusOptions,
    tabOptions,
    getCustomerSessions,
    getCustomerInteractions,
    getCustomerReviews,
    getCustomerDetails,
    fullName,
    onCustomerStatusChange,
    selectedCustomerStatus,
  };
}
